import { useCallback } from 'react';
import { applyNodeChanges, type Node, type NodeChange } from '@xyflow/react';
import type { ImplementationPlanColumn } from '@mm-shared/types/Objective1';
import { rankBetween } from '../utils/fractionalRank';
import { computeLayout, columnFromX, CARD_WIDTH } from '../utils/planLayout';
import type { PlanCardView } from '../utils/planLayout';

/**
 * usePlanNodesChangeHandler — commit-on-drop drag semantics for the board
 * (ADR §2 event table, §1d tied-group rule, §1e write shape).
 *
 * | Event | Behavior |
 * |---|---|
 * | position && dragging === true  | applyNodeChanges locally, x/y clamped. No dispatch. |
 * | position && dragging === false | Resolve (column, slot); apply §1d; compute rankBetween; snap; ONE onMoveCard. |
 * | same (column, slot)            | Snap back. No dispatch. |
 * | rankBetween guard/cap throw    | Snap back. No dispatch (defense-in-depth; unreachable via §1d). |
 *
 * Follows useNodesChangeHandler in map_dependencies: a reducer over changes with
 * a drag branch and a drop branch; everything else falls through to
 * applyNodeChanges unchanged.
 *
 * Under the hide-done filter (ADR.ImplementationPlansUX2 C5c/C5d/C6c) three
 * things join that table without changing it: the drag branch captures the
 * hidden projection once (`hiddenFreezeRef`) so every geometric read below
 * sees the layout the user was looking at; both resolvers keep everything
 * geometric on the VISIBLE axis; and each crosses to the column's full rank
 * order exactly once, through `rankAgainstFullOrder`.
 */

export type MoveCardHandler = (
  objectiveUuid: string,
  column: ImplementationPlanColumn,
  rank: string,
) => void;

export type PlanNodesChangeHandlerOptions = {
  /**
   * The board's cards as it currently views them (ADR B2). `PlanCardView`, not
   * `PlanCard`: the drop resolver and the renderer must consume the same array,
   * or a collapsed card's height could reach one without reaching the other.
   */
  cards: PlanCardView[];
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>;
  onMoveCard: MoveCardHandler;
  /** Suppresses layout-key rebuilds mid-drag (ADR §2 reconciliation). */
  isDraggingRef: React.MutableRefObject<boolean>;
  /**
   * The uuid of the card currently being dragged, or null. Lets the canvas's
   * rebuild effect detect a drag aborted without a dragging:false change (the
   * dragged objective deleted mid-drag by a collaborator) and un-suppress.
   */
  draggedIdRef: React.MutableRefObject<string | null>;
  /**
   * The hidden projection, frozen for the duration of a drag
   * (ADR.ImplementationPlansUX2 C6c). `null` when no drag is in flight.
   *
   * Captured here — once, in the `dragging` branch below — and consumed by the
   * canvas's `projectView` as `frozenHidden`, released by the canvas in
   * `onDropSettled` (and in its aborted-drag escape). The handler's `cards`
   * **is** the canvas's `viewCards`, so the snapshot is literally what is on
   * screen when the pointer goes down.
   *
   * It defends TWO cards, not one. The dragged card: without it a remote
   * `delivered` flip mid-drag hides it, `heightOf` returns 0 so the clamp is
   * wrong, `draggedPosition` is undefined so `dropMidY` becomes the top edge
   * instead of the midpoint, and the settled-layout snap finds no position.
   * And any sibling: a collaborator completing a NON-dragged card reflows the
   * survivors, so the drop resolves against positions that were never
   * rendered and the card silently crosses a neighbour.
   */
  hiddenFreezeRef: React.MutableRefObject<ReadonlySet<string> | null>;
  /** Called after a drop settles so the canvas can apply a deferred remote echo. */
  onDropSettled?: () => void;
};

/**
 * Translates a **visible** gap into a rank on the column's **full** order
 * (ADR.ImplementationPlansUX2 C5c step 3) — the one place either resolver
 * crosses from the visible axis to the rank axis, and the whole of what makes
 * a filtered move correct in the data as well as on screen.
 *
 * In words: *the card lands immediately before the visible card it was
 * dropped/moved above (`va`), or at the very end of the column when there is
 * none; and if that lands inside a run of equal-ranked cards, it backs out
 * above the run.*
 *
 * Four properties, each load-bearing:
 *
 * - **It reduces to the shipped code when nothing is hidden.** Full list =
 *   visible list, so `i` is the post-§1d slot, `full[i - 1]` is exactly the
 *   `before` the §1d walk produced, and the `while` never fires — the walk
 *   already guarantees the two sides of the gap are untied. The regression
 *   surface is the filtered case alone.
 * - **The visible order survives lifting the filter.** X dropped between
 *   visible A and B with hidden H1 H2 between them yields `A, H1, H2, X, B`,
 *   which filters back to `A, X, B` — the gesture.
 * - **It cannot manufacture an illegal rank.** After the `while`, `full[i-1]`
 *   and `full[i]` have different ranks, and the array is sorted by
 *   (rank, uuid), so `before < after` — `rankBetween`'s precondition
 *   (`fractionalRank.ts`, which THROWS when `before >= after`) is satisfied by
 *   construction. If every card is tied, `i` reaches 0 and the call is
 *   `rankBetween(undefined, first)`, a legal prepend.
 * - **It terminates.** Each iteration strictly decreases `i`, bounded below
 *   by 0.
 *
 * Anchoring on the gap's *upper* card (`va`) rather than its lower one is what
 * reconciles the pointer and keyboard paths (C5d, Alternative L3): a drop
 * below every visible card and keyboard `'column'` are the same
 * `va === undefined` branch, and keyboard up-then-down is idempotent on the
 * visible order.
 */
function rankAgainstFullOrder(
  fullColumnOrder: PlanCardView[],
  movedUuid: string,
  va: { uuid: string } | undefined,
): { kind: 'rank'; rank: string } | { kind: 'error' } {
  const full = fullColumnOrder.filter((c) => c.uuid !== movedUuid);
  let i = va ? full.findIndex((c) => c.uuid === va.uuid) : full.length;
  while (i > 0 && full[i - 1].effectiveRank === full[i]?.effectiveRank) i--;
  try {
    return { kind: 'rank', rank: rankBetween(full[i - 1]?.effectiveRank, full[i]?.effectiveRank) };
  } catch {
    // Defense-in-depth (§1b guard/cap). Unreachable given the caller contract
    // and the legality property above; if it ever fires the gesture is
    // discarded and the card snaps back (no write).
    return { kind: 'error' };
  }
}

/**
 * Resolves a drop to its neighbors and target rank per ADR §2 + §1d, and —
 * under a filter — per ADR.ImplementationPlansUX2 C5c's four steps:
 *
 * 0. the no-op guard reads the **visible** axis;
 * 1. geometry is unchanged, over positioned (therefore visible) siblings;
 * 2. the §1d tied-group walk stays on those same visible siblings;
 * 3. exactly one translation crosses to the full order, with a tie-extension
 *    pass (`rankAgainstFullOrder`).
 *
 * Exported for direct unit testing.
 */
export function resolveDrop(
  cards: PlanCardView[],
  draggedUuid: string,
  dropX: number,
  dropY: number,
):
  | { kind: 'noop' }
  | { kind: 'move'; column: ImplementationPlanColumn; rank: string }
  | { kind: 'error' } {
  const layout = computeLayout(cards);
  const dragged = cards.find((c) => c.uuid === draggedUuid);
  if (!dragged) return { kind: 'noop' };

  const draggedPosition = layout.cards.find((cp) => cp.card.uuid === draggedUuid);
  const draggedHeight = draggedPosition?.height ?? 0;
  const dropMidY = dropY + draggedHeight / 2;

  const targetColumn = columnFromX(dropX);

  // STEP 1 — geometry, unchanged. Target column's cards in display order,
  // excluding the dragged card, with their static positions (siblings do not
  // reflow mid-drag — ADR §2). `layout.cards` holds POSITIONED cards only, so
  // this is already the visible set: a hidden card has no position and cannot
  // participate in a midpoint comparison (C5c, Alternative J).
  const siblings = layout.cards.filter(
    (cp) => cp.card.column === targetColumn && cp.card.uuid !== draggedUuid,
  );

  // Insertion slot: number of siblings whose vertical midpoint is above the drop.
  let slot = 0;
  for (const sibling of siblings) {
    if (sibling.y + sibling.height / 2 < dropMidY) slot++;
  }

  // STEP 0 — the no-op guard, on the VISIBLE axis (C5c). `slot` is an index
  // into the visible siblings, so the only index it may be compared against is
  // one into the visible order. The shipped guard compared it against
  // `columnCards` — an identity that held only while the two sets were equal,
  // and which under a filter does BOTH possible wrong things:
  //
  //   A. lost gesture   [H, A, D, B], H hidden. Full-order currentSlot(D) = 2;
  //      dragging D to the visual bottom gives slot = 2, the guard fires, the
  //      card snaps back and a real move is silently discarded. Visible
  //      currentSlot = 1, so the gesture survives.
  //   B. phantom write  [A, H, D], H hidden. Full-order currentSlot(D) = 2;
  //      dropping D exactly where it was gives slot = 1, the guard does not
  //      fire, and a write reorders D above H — from a gesture that changed
  //      nothing on screen. Visible currentSlot = 1 = slot: a no-op.
  //
  // The guard also depends on the dragged card being visible, which C6c's
  // frozen projection guarantees for the whole drag.
  if (targetColumn === dragged.column) {
    const currentSlot = layout.visibleColumnCards[dragged.column].findIndex(
      (c) => c.uuid === draggedUuid,
    );
    if (slot === currentSlot) return { kind: 'noop' };
  }

  let before = slot > 0 ? siblings[slot - 1] : undefined;
  let after = slot < siblings.length ? siblings[slot] : undefined;

  // STEP 2 — the §1d tied-group walk, kept byte-for-byte and kept on the
  // VISIBLE siblings (C5c). It is GEOMETRIC: it reads `first.y`, `last.y`,
  // `last.height` and branches on `dropMidY < groupMidY`. Run over the full
  // order it would put a position-less card in `last`, make `groupMidY` NaN,
  // make `dropMidY < NaN` unconditionally false, and collapse the direction
  // decision to "always below the group" regardless of the pointer
  // (Alternative L2). Over `siblings` every term is a CardPosition and NaN is
  // unreachable. Its output is a visible gap (before, after), either side
  // possibly absent; step 3 consumes `after`.
  //
  // Tied-group rule (§1d): never place inside a group of equal-ranked cards. When
  // both neighbors share a rank, place before or after the whole group — whichever
  // side of the group's vertical midpoint the pointer released on — and rank
  // against the neighbor outside the group.
  if (before && after && before.card.effectiveRank === after.card.effectiveRank) {
    const tiedRank = before.card.effectiveRank;
    let groupStart = slot - 1;
    while (groupStart > 0 && siblings[groupStart - 1].card.effectiveRank === tiedRank) {
      groupStart--;
    }
    let groupEnd = slot;
    while (groupEnd + 1 < siblings.length && siblings[groupEnd + 1].card.effectiveRank === tiedRank) {
      groupEnd++;
    }
    const first = siblings[groupStart];
    const last = siblings[groupEnd];
    const groupMidY = (first.y + last.y + last.height) / 2;
    if (dropMidY < groupMidY) {
      before = groupStart > 0 ? siblings[groupStart - 1] : undefined;
      after = first;
    } else {
      before = last;
      after = groupEnd + 1 < siblings.length ? siblings[groupEnd + 1] : undefined;
    }
  }

  // STEP 3 — translate the visible gap into the full order, then back out of
  // any tied run. `before` is deliberately not passed: step 3 re-derives the
  // predecessor as `full[i - 1]`, which equals `before` exactly whenever
  // nothing is hidden (the reduction property) and is the only correct answer
  // when something is — a tied run whose other members are hidden is invisible
  // to the geometric walk above, and `rankBetween(H2, V)` on equal ranks would
  // throw and silently discard the drop.
  const translated = rankAgainstFullOrder(
    layout.columnCards[targetColumn],
    draggedUuid,
    after?.card,
  );
  if (translated.kind === 'error') return { kind: 'error' };
  return { kind: 'move', column: targetColumn, rank: translated.rank };
}

export type KeyboardMoveAction =
  | { type: 'up' }
  | { type: 'down' }
  | { type: 'column'; column: ImplementationPlanColumn };

/**
 * Resolves a keyboard move to the same {move|noop|error} union as resolveDrop
 * (ADR §6). Keyboard moves share onMoveCard with the pointer path, so they
 * inherit the one-write invariant and the §1e write shape.
 *
 * **One rule, not two** (ADR.ImplementationPlansUX2 C5d). Every branch below
 * resolves a *visible* gap and then runs C5c step 3 verbatim
 * (`rankAgainstFullOrder`):
 *
 * - **'up' / 'down'** re-rank against the neighbours at the adjacent slot in
 *   the card's own column's **visible** order. A press must move the card past
 *   something the user can SEE; resolving over the full order would let it
 *   swap with a hidden neighbour and appear to do nothing (INV-10). The
 *   no-op boundaries follow the visible list too — the last visible card
 *   cannot move down even when hidden cards follow it. A tie at the target gap
 *   is resolved by direction — moving up lands before the tied group, moving
 *   down lands after it (§1d; no geometry here, so no NaN risk).
 * - **'column' is NOT a special case.** It is the `va === undefined` branch of
 *   step 3: `i = full.length`, so the card lands after the target column's
 *   full-order last card, trailing hidden run included. A *pointer* drop below
 *   all visible cards, or into a column whose cards are all hidden, produces
 *   `va === undefined` too and lands in the same place. Moving to the current
 *   column is a no-op.
 *
 * A hidden card has no node and no controls, so it cannot be moved: there is
 * no key handler to press. Reached anyway, its visible `currentSlot` is -1 and
 * `'up'`/`'down'` are no-ops; `'column'` would still resolve, exactly as it
 * does for any card, which is why no extra guard is written for a state the UI
 * cannot produce.
 */
export function resolveKeyboardMove(
  cards: PlanCardView[],
  cardUuid: string,
  action: KeyboardMoveAction,
):
  | { kind: 'noop' }
  | { kind: 'move'; column: ImplementationPlanColumn; rank: string }
  | { kind: 'error' } {
  const layout = computeLayout(cards);
  const card = cards.find((c) => c.uuid === cardUuid);
  if (!card) return { kind: 'noop' };

  const targetColumn = action.type === 'column' ? action.column : card.column;
  /** The gap's upper card — C5c step 3's anchor. `undefined` means "the end". */
  let after: PlanCardView | undefined;

  if (action.type === 'column') {
    if (action.column === card.column) return { kind: 'noop' };
    // End of the target column: the same anchor a pointer drop below every
    // visible card produces. This is where the two paths MEET rather than
    // where they diverge (C5d).
    after = undefined;
  } else {
    // 'up' / 'down' within the card's own column, on the VISIBLE axis.
    const column = layout.visibleColumnCards[card.column];
    const currentSlot = column.findIndex((c) => c.uuid === cardUuid);
    if (currentSlot === -1) return { kind: 'noop' };
    if (action.type === 'up' && currentSlot === 0) return { kind: 'noop' };
    if (action.type === 'down' && currentSlot === column.length - 1) return { kind: 'noop' };

    // Siblings excluding the moved card; target slot in that list.
    const siblings = column.filter((c) => c.uuid !== cardUuid);
    const targetSlot = action.type === 'up' ? currentSlot - 1 : currentSlot + 1;

    // `before` is the gap's lower card. Kept because the §1d walk below is the
    // shipped block unchanged and reads it; step 3 re-derives the predecessor
    // from the full order, so only `after` leaves this branch (C5c step 3).
    let before = targetSlot > 0 ? siblings[targetSlot - 1] : undefined;
    after = targetSlot < siblings.length ? siblings[targetSlot] : undefined;

    // §1d tie at the target gap: direction picks the side of the tied group.
    if (before && after && before.effectiveRank === after.effectiveRank) {
      const tiedRank = before.effectiveRank;
      let groupStart = targetSlot - 1;
      while (groupStart > 0 && siblings[groupStart - 1].effectiveRank === tiedRank) {
        groupStart--;
      }
      let groupEnd = targetSlot;
      while (groupEnd + 1 < siblings.length && siblings[groupEnd + 1].effectiveRank === tiedRank) {
        groupEnd++;
      }
      if (action.type === 'up') {
        before = groupStart > 0 ? siblings[groupStart - 1] : undefined;
        after = siblings[groupStart];
      } else {
        before = siblings[groupEnd];
        after = groupEnd + 1 < siblings.length ? siblings[groupEnd + 1] : undefined;
      }
    }
  }

  const translated = rankAgainstFullOrder(layout.columnCards[targetColumn], cardUuid, after);
  if (translated.kind === 'error') return { kind: 'error' };
  return { kind: 'move', column: targetColumn, rank: translated.rank };
}

export function usePlanNodesChangeHandler({
  cards,
  setNodes,
  onMoveCard,
  isDraggingRef,
  draggedIdRef,
  hiddenFreezeRef,
  onDropSettled,
}: PlanNodesChangeHandlerOptions) {
  return useCallback(
    (changes: NodeChange[]) => {
      // ALL side effects (onMoveCard dispatch, ref mutations, onDropSettled)
      // happen here, OUTSIDE the setNodes updater. React.StrictMode double-
      // invokes state updaters in development; a dispatch inside the updater
      // would fire twice per drop (PR #6 review finding). The updaters passed
      // to setNodes below are pure.
      const layout = computeLayout(cards);
      const heightOf = (id: string) =>
        layout.cards.find((cp) => cp.card.uuid === id)?.height ?? 0;
      const clamp = (position: { x: number; y: number }, height: number) => ({
        x: Math.max(0, Math.min(layout.canvasWidth - CARD_WIDTH, position.x)),
        y: Math.max(0, Math.min(layout.canvasHeight - height, position.y)),
      });

      const passthrough: NodeChange[] = [];
      // PlanCardView, deliberately (ADR B2's most consequential widening).
      // Left as PlanCard[] this annotation would still accept the
      // `cards.map(...)` assignment below — a PlanCardView is assignable to a
      // PlanCard — but it would erase `collapse` from the inferred element
      // type, and the settled-layout snap at the bottom of this handler would
      // recompute every position at EXPANDED height one frame after each drop.
      let settledCards: PlanCardView[] | null = null;

      for (const change of changes) {
        if (change.type !== 'position' || !change.position) {
          passthrough.push(change);
          continue;
        }

        if (change.dragging) {
          isDraggingRef.current = true;
          draggedIdRef.current = change.id;
          // C6c: capture the whole hidden projection ONCE at drag start —
          // guarded on `=== null` because this branch fires every frame.
          // UNCONDITIONALLY, including when the hidden set is empty: an empty
          // snapshot *pins* the empty set, which is what stops a mid-drag
          // remote flip hiding a sibling on a `hideDone`-on column where
          // nothing was done yet. Its cost is that every drop then lifts a
          // freeze and bumps `rebuildTick` — one extra projection recompute,
          // batched with the optimistic move.
          if (hiddenFreezeRef.current === null) {
            hiddenFreezeRef.current = new Set(
              cards.filter((c) => c.hidden).map((c) => c.uuid),
            );
          }
          passthrough.push({
            ...change,
            position: clamp(change.position, heightOf(change.id)),
          });
          continue;
        }

        // The drop.
        isDraggingRef.current = false;
        draggedIdRef.current = null;
        const dropPosition = clamp(change.position, heightOf(change.id));
        const resolution = resolveDrop(cards, change.id, dropPosition.x, dropPosition.y);

        settledCards = cards;
        if (resolution.kind === 'move') {
          onMoveCard(change.id, resolution.column, resolution.rank);
          settledCards = cards.map((c) =>
            c.uuid === change.id
              ? {
                  ...c,
                  column: resolution.column,
                  effectiveRank: resolution.rank,
                  hasStoredRank: true,
                }
              : c,
          );
        }
      }

      const settledLayout = settledCards !== null ? computeLayout(settledCards) : null;
      if (settledLayout !== null || passthrough.length > 0) {
        // ONE pure updater — safe under StrictMode double-invocation. It applies
        // passthrough changes, then (after a drop) snaps every card to the
        // settled layout: the moved card lands in its slot, a no-op/error drop
        // snaps back, and siblings reflow as a pure recompute.
        setNodes((nodes) => {
          let next =
            passthrough.length > 0
              ? (applyNodeChanges(passthrough, nodes) as Node[])
              : nodes;
          if (settledLayout !== null) {
            next = next.map((n) => {
              const cp = settledLayout.cards.find((c) => c.card.uuid === n.id);
              return cp ? { ...n, position: { x: cp.x, y: cp.y } } : n;
            });
          }
          return next;
        });
      }
      if (settledLayout !== null) {
        onDropSettled?.();
      }
    },
    [
      cards,
      setNodes,
      onMoveCard,
      isDraggingRef,
      draggedIdRef,
      hiddenFreezeRef,
      onDropSettled,
    ],
  );
}
